import type { GeometryElement } from 'jsxgraph';
import type { LabelDisplayStyle, ResolvedLabelConfig } from '@examdojo/core/widget';
import { bindLabel } from '@examdojo/core/widget';
import type { ElementDisplayConfig, LabelElementParams, PrimitiveElementDef } from '../../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator } from '../element.model';
import { resolveFeatureVisibility } from '../features/feature-style.model';
import { resolvePosition } from './position';

/**
 * Creates a standalone label (JSXGraph text element).
 *
 * Params define identity: text content, precision, element attachment (of), position.
 * Display defines styling: variant, color, visible, position (anchor), offset.
 *
 * Follows the same `of` pattern as features — `of` attaches the label to an element.
 */
export class LabelCreator implements ElementCreator<PrimitiveElementDef> {
  readonly types = ['label'];

  create(
    id: string,
    def: PrimitiveElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, themeService, featureTheme } = context;
    const params = def.params as LabelElementParams;
    const objects: GeometryElement[] = [];

    // Resolve display style
    const labelStyle = this.resolveLabelDisplay(context, display);
    const visible = resolveFeatureVisibility('label', featureTheme, display, []);

    // Resolve position: attached element or standalone
    let getX: () => number;
    let getY: () => number;
    let referencedElement: GeometryElement | undefined;
    let mutablePositionX: { get: () => number; set: (v: number) => void } | undefined;
    let mutablePositionY: { get: () => number; set: (v: number) => void } | undefined;

    if (params.of) {
      const element = context.elements.get(params.of);
      if (!element) {
        throw new Error(`Label '${id}' references element '${params.of}' which does not exist`);
      }
      const primary = element.objects.find((obj) => typeof (obj as unknown as { X: unknown }).X === 'function');
      if (primary) {
        referencedElement = primary;
        const typed = primary as unknown as { X: () => number; Y: () => number };
        getX = () => typed.X();
        getY = () => typed.Y();
      } else {
        getX = () => NaN;
        getY = () => NaN;
      }
    } else if (params.position) {
      [getX, getY] = resolvePosition(params.position, context);

      // For literal number coordinates, make them mutable so UPDATE_PARAM can animate them.
      // Expression coordinates (e.g., "pt.x") remain reactive thunks.
      if (typeof params.position.x === 'number') {
        let mutableX = params.position.x;
        getX = () => mutableX;
        mutablePositionX = {
          get: () => mutableX,
          set: (v: number) => {
            mutableX = v;
          },
        };
      }
      if (typeof params.position.y === 'number') {
        let mutableY = params.position.y;
        getY = () => mutableY;
        mutablePositionY = {
          get: () => mutableY,
          set: (v: number) => {
            mutableY = v;
          },
        };
      }
    } else {
      getX = () => 0;
      getY = () => 0;
    }

    // Create invisible anchor point for bindLabel positioning.
    // Explicit transparent colors + size 0 prevent the anchor from rendering
    // even if PATCH_DISPLAY applies `visible: true` to all label objects.
    const anchor = board.create('point', [getX, getY], {
      visible: false,
      fixed: true,
      withLabel: false,
      size: 0,
      fillColor: 'transparent',
      strokeColor: 'transparent',
      fillOpacity: 0,
    }) as GeometryElement;
    objects.push(anchor);

    // Build ResolvedLabelConfig from params + display cascade
    const resolvedConfig: ResolvedLabelConfig = {
      visible: visible !== false,
      variant: labelStyle.variant ?? 'tertiary',
      color: labelStyle.color ?? 'neutral',
      position: labelStyle.position ?? 'center',
      positionIsExplicit: labelStyle.position !== undefined,
      contentVariant: 'name',
      text: params.text,
      offset: labelStyle.offset ?? [0, 0],
      precision: params.precision ?? context.defaultPrecision,
      popup: labelStyle.popup,
      fontSize: labelStyle.fontSize,
      textAlign: labelStyle.textAlign,
      spacing: labelStyle.spacing ?? 'normal',
    };

    // Build resolveValue callback from valueContext
    const resolveValue = context.valueContext
      ? (path: string): number | string | undefined => {
          if (context.valueContext!.has(path)) {
            return context.valueContext!.getValue(path);
          }
          if (context.valueContext!.hasText(path)) {
            return context.valueContext!.getTextValue(path);
          }
          return undefined;
        }
      : undefined;

    // Create label via bindLabel — single rendering pipeline
    const label = bindLabel({
      ctx: { board: context.board, container: context.container, resolveValue },
      themeService,
      element: anchor,
      config: resolvedConfig,
      popupTarget: referencedElement,
    });
    objects.push(label);

    const result: CreatedElement = {
      id,
      type: 'label',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };

    // Expose getParams/setParams for mutable position coordinates (enables UPDATE_PARAM animation)
    if (mutablePositionX || mutablePositionY) {
      result.getParams = () => {
        const p: Record<string, number> = {};
        if (mutablePositionX) {
          p['position.x'] = mutablePositionX.get();
        }
        if (mutablePositionY) {
          p['position.y'] = mutablePositionY.get();
        }
        return p;
      };
      result.setParams = (newParams: Partial<Record<string, number>>) => {
        if (mutablePositionX && 'position.x' in newParams) {
          mutablePositionX.set(newParams['position.x']!);
        }
        if (mutablePositionY && 'position.y' in newParams) {
          mutablePositionY.set(newParams['position.y']!);
        }
        board.update();
      };
    }

    return result;
  }

  private resolveLabelDisplay(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
  ): LabelDisplayStyle & { popup?: boolean } {
    // Cascade: featureTheme['label'] → display
    const featureNode = context.featureTheme['label'];
    const featureLabel = featureNode?.label ?? {};
    const displayLabel = display?.label ?? {};
    return { ...featureLabel, ...displayLabel };
  }
}
